import { useApolloClient } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FileUpload } from '@shared/components/blocks/types';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { DEFAULT_MIME_TYPE, uploadFile } from '@shared/scenes/upload/store/actions';
import { MBUploadUtil, MBUploadUtilFile } from '@shared/scenes/upload/util';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';

export interface UseFileUploadResult {
  handleUploadFile(files: MBUploadUtilFile[]): void;
  handleDrop(file: File[]): void;
  handleClear(file: FileUpload): void;
  showFilePicker(): void;
  uploading: boolean;
}

export interface UseFileUploadProps {
  onFileAdded(file: FileUpload[]): void;
  onFileRemoved(file: FileUpload): void;
  acceptedContentTypes?: string[];
}

export function useFileUpload({
  onFileAdded,
  onFileRemoved,
  acceptedContentTypes,
}: UseFileUploadProps): UseFileUploadResult {
  const client = useApolloClient();
  const [uploading, setUploading] = useState(false);

  const validateUploads = useValidateUploads({
    showUpgradeModal: true,
  });

  const handleUploadFile = useCallback(
    async (files: MBUploadUtilFile[]) => {
      setUploading(true);

      try {
        let uploadedFiles: FileUpload[] = [];
        for (const file of files) {
          const isFileValid = !!validateUploads([file]);
          if (!isFileValid) {
            continue;
          }

          const url = await uploadFile(client, file);
          uploadedFiles.push({
            url,
            name: file.name,
            contentType: file.contentType || DEFAULT_MIME_TYPE,
            width: file.width,
            height: file.height,
          });
        }
        onFileAdded(uploadedFiles);
      } catch (err) {
        console.error(err);
        MBAlert.alert({
          message: i18n('card.blocks.image.uploadFailed'),
        });
      } finally {
        setUploading(false);
      }
    },
    [client, onFileAdded, validateUploads],
  );

  const showFilePicker = useCallback(async () => {
    const files = await MBUploadUtil.selectFiles({ multiple: true, acceptedContentTypes });
    if (!isNil(files)) {
      handleUploadFile(files);
    }
  }, [acceptedContentTypes, handleUploadFile]);

  const handleClear = useCallback((file: FileUpload) => onFileRemoved(file), [onFileRemoved]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (files?.length) {
        let droppedFiles: MBUploadUtilFile[] = [];
        for (const file of files) {
          const dims = await MBUploadUtil.getWebFileDimensions(file);
          droppedFiles.push({
            id: String(Date.now()),
            url: URL.createObjectURL(file),
            name: file.name,
            contentType: file.type,
            size: file.size,
            ...(dims ?? {}),
          });
        }
        handleUploadFile(droppedFiles);
      }
    },
    [handleUploadFile],
  );

  const value = useMemo<UseFileUploadResult>(
    () => ({
      uploading,
      handleUploadFile,
      handleDrop,
      handleClear,
      showFilePicker,
    }),
    [uploading, handleUploadFile, handleDrop, handleClear, showFilePicker],
  );

  return value;
}
